import { gzipSync } from "fflate";
import parseIdToken from "@warda/library-ui/contexts/AppCookiesContext/utils/parseIdToken";
import { retrieveUserData } from "@warda/library-ui/contexts/AppUserDataContext";
import { BASE_URL } from "../constants/keys";
import authCookiesHelper from "../constants/authCookiesHelper";
export const requestTokenWithRefreshToken = async () => {
    const userData = retrieveUserData();
    const body = JSON.stringify({
        refreshToken: authCookiesHelper.getRefreshToken(),
        applicationId: "seecommerce",
        ...userData,
    });
    const response = await fetch(`${BASE_URL}/users/token/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
    });
    const { status, statusText } = response;
    if (status >= 400)
        return { status, statusText, response };
    const res = await response.json();
    authCookiesHelper.setCookies(res);
    return { status, statusText, response };
};
export const silentTestAndRefresh = async () => {
    const isRefreshRequired = () => {
        const jwt = parseIdToken(authCookiesHelper.getIdToken());
        if (!!jwt) {
            const dateExpiresJwt = Number(`${jwt.exp}000`);
            const dateNow = Date.now();
            return dateNow + 1000 > dateExpiresJwt;
        }
        else {
            return true;
        }
    };
    if (isRefreshRequired()) {
        try {
            await requestTokenWithRefreshToken();
        }
        catch (error) {
            console.error("Refresh failed", error);
            return false;
        }
    }
    return true;
};
const getOptions = ({ method, jsonBody = {}, formData = false, }) => {
    const headers = formData ? undefined : { "Content-Type": "application/json" };
    const useAuthHeader = authCookiesHelper.isApiGatewayAccessWithAuthorizationHeader();
    const options = useAuthHeader
        ? {
            method,
            headers: {
                ...headers,
                Authorization: authCookiesHelper.getAuthorization(),
            },
        }
        : {
            method,
            headers,
            credentials: "include",
        };
    if (method !== "GET" && method !== "HEAD" && jsonBody !== undefined) {
        if (formData && jsonBody instanceof FormData) {
            options.body = jsonBody;
        }
        else if (typeof jsonBody === "string") {
            options.body = jsonBody;
        }
        else {
            options.body = JSON.stringify(jsonBody);
        }
    }
    return options;
};
export const fetchCookieJwtWithRefreshToken = async ({ url, method = "GET", jsonBody = {}, formData = false, blob = false, }) => {
    const response = await fetch(url, getOptions({ method, jsonBody, formData }));
    const { status } = response;
    if (status === 401) {
        const refToken = await requestTokenWithRefreshToken();
        if (refToken.status >= 400)
            throw refToken;
        const retryResponse = await fetch(url, getOptions({ method, jsonBody, formData }));
        if (retryResponse.status >= 400)
            throw { status: retryResponse.status, response: retryResponse };
        return blob ? await retryResponse.blob() : await retryResponse.json();
    }
    if (status >= 400)
        throw { status, response };
    return blob ? await response.blob() : await response.json();
};
export const fetchCompressedJwtWithRefreshToken = async ({ url, method = "PUT", jsonBody, formData = false, blob = false, }) => {
    const compressBody = (body) => {
        const encoded = typeof body === "string"
            ? new TextEncoder().encode(body)
            : new TextEncoder().encode(JSON.stringify(body));
        const compressed = gzipSync(encoded);
        // Slice crea un nuovo ArrayBuffer standard compatibile con Blob
        const pureArray = compressed.slice();
        return new Blob([pureArray], { type: "application/json" });
    };
    const buildOptions = () => {
        const isApiGateway = authCookiesHelper.isApiGatewayAccessWithAuthorizationHeader();
        const baseHeaders = formData
            ? {}
            : {
                "Content-Type": "application/json",
                "Content-Encoding": "gzip",
            };
        if (isApiGateway) {
            baseHeaders["Authorization"] = authCookiesHelper.getAuthorization();
        }
        let body;
        if (method !== "GET" && method !== "HEAD" && jsonBody !== undefined) {
            if (formData && jsonBody instanceof FormData) {
                body = jsonBody;
            }
            else {
                body = compressBody(jsonBody);
            }
        }
        const options = {
            method,
            headers: baseHeaders,
            credentials: isApiGateway ? undefined : "include",
            body,
        };
        return options;
    };
    const fetchWithRetry = async () => {
        let response = await fetch(url, buildOptions());
        if (response.status === 401) {
            const refToken = await requestTokenWithRefreshToken();
            if (refToken.status >= 400)
                throw refToken;
            response = await fetch(url, buildOptions());
        }
        if (response.status >= 400)
            throw { status: response.status, response };
        return response;
    };
    const response = await fetchWithRetry();
    return blob ? await response.blob() : await response.json();
};
